import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSlate } from 'slate-react';
import cx from 'classnames';

import { getDomFirstSelectionRange } from 'src/lib/domUtils';
import { isNonFocusedEditorSelectionValid, HOVERING_TOOLBAR_POSITIONS } from '../../utils';
import useStyles from './useStyles';

const preventDefault = (e) => e.preventDefault();

const getElementPosition = (position, element, rect, parentRect) => {
  // POSITIONS.top:
  const topPosition = rect.top - parentRect.top - element.offsetHeight - 10;

  // POSITIONS.bottom:
  const bottomPosition = rect.top - parentRect.top + rect.height + 10;

  const styleTop = position === HOVERING_TOOLBAR_POSITIONS.top ? topPosition : bottomPosition;

  let styleLeft = rect.left - parentRect.left;
  styleLeft =
    styleLeft + element.offsetWidth > parentRect.width
      ? rect.right - element.offsetWidth - parentRect.left
      : styleLeft;

  return {
    left: styleLeft,
    top: styleTop,
  };
};

export const HoveringToolbar = ({
  children,
  className,
  position,
  onDismiss,
  preventOnMouseDownEvent,
  forceVisibility,
}) => {
  const ref = useRef();
  const editor = useSlate();
  const styles = useStyles();

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return;
    }

    if (!isNonFocusedEditorSelectionValid(editor)) {
      el.removeAttribute('style');
      if (onDismiss) {
        onDismiss();
      }
      return;
    }

    const selectionRange = getDomFirstSelectionRange();
    if (!selectionRange) {
      return;
    }

    const rect = selectionRange.getBoundingClientRect();
    const parent = el.parentElement.parentElement;
    const parentRect = parent.getBoundingClientRect();
    el.style.opacity = '1';

    const { top, left } = getElementPosition(position, el, rect, parentRect);
    if (forceVisibility && (top < 0 || left < 0)) return;

    el.style.top = top + 'px';
    el.style.left = left + 'px';
  });

  return (
    <div
      ref={ref}
      className={cx(styles.root, className)}
      onMouseDown={preventOnMouseDownEvent ? undefined : preventDefault}
    >
      {children}
    </div>
  );
};

HoveringToolbar.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  position: PropTypes.oneOf(Object.values(HOVERING_TOOLBAR_POSITIONS)),
  onDismiss: PropTypes.func,
  preventOnMouseDownEvent: PropTypes.bool,
  forceVisibility: PropTypes.bool,
};
HoveringToolbar.defaultProps = {
  className: '',
  position: 'top',
  onDismiss: null,
  preventOnMouseDownEvent: true,
  forceVisibility: false,
};
